import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useLanguage } from '../contexts/LanguageContext';
import { ChevronLeft } from 'lucide-react';
import branding from '../config/branding';
import { useCategories, UP_CATEGORY } from '../lib/taxonomy';
import { ArticleFeed } from '../components/news/ArticleFeed';
import { FilterChips } from '../components/news/FilterChips';

export default function CategoryPage() {
  const { category } = useParams();
  const { isHindi } = useLanguage();
  const { byId, childrenOf, label } = useCategories();

  // Rajya/Sahar has its own page with the mandal → district filter
  if (category === UP_CATEGORY) return <Navigate to="/rajya" replace />;

  const current = byId[category];
  const parent = current?.parent_id ? byId[current.parent_id] : null;
  // A top-level category with subcategories (e.g. Other) shows them as chips; so do its children
  const chipParent = parent || current;
  const subcategories = chipParent ? childrenOf(chipParent.id) : [];
  const categoryName = label(category, isHindi);

  const chips = subcategories.length > 0
    ? [
        {
          key: chipParent.id,
          label: isHindi ? 'सभी' : 'All',
          to: `/category/${chipParent.id}`,
          active: category === chipParent.id,
        },
        ...subcategories.map((c) => ({
          key: c.id,
          label: isHindi ? c.name_hi : c.name,
          to: `/category/${c.id}`,
          active: c.id === category,
        })),
      ]
    : null;

  return (
    <div className="min-h-screen bg-white" data-testid="category-page">
      <Helmet>
        <title>{`${categoryName} ${isHindi ? 'समाचार' : 'News'} | ${isHindi ? branding.nameHi : branding.name}`}</title>
        <meta name="description" content={isHindi ? `${categoryName} की नवीनतम खबरें और अपडेट।` : `Read the latest ${categoryName} news, updates, and analysis.`} />
        <meta property="og:title" content={`${categoryName} News | ${branding.name}`} />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 pt-4 sm:pt-6">
        <Link
          to={parent ? `/category/${parent.id}` : '/'}
          className="inline-flex items-center gap-0.5 min-h-[40px] kicker text-[var(--ink-muted)] hover:text-[var(--ink)]"
        >
          <ChevronLeft className="w-3.5 h-3.5" />
          <span className={isHindi ? 'font-hindi' : ''}>
            {parent ? label(parent.id, isHindi) : (isHindi ? 'होम' : 'Home')}
          </span>
        </Link>
        <h1 className={`text-3xl sm:text-5xl font-bold text-[var(--ink)] pb-4 border-b border-[var(--ink)] ${isHindi ? 'font-hindi-heading' : 'font-heading'}`}>
          {categoryName}
        </h1>
      </div>

      {chips && (
        <div className="max-w-7xl mx-auto px-4 pt-5 sm:pt-8">
          <FilterChips items={chips} label={isHindi ? 'उप-श्रेणियां' : 'Subcategories'} />
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 py-5 sm:py-8">
        <ArticleFeed
          params={{ category }}
          emptyText={isHindi ? `${categoryName} में कोई समाचार नहीं है` : `No articles in ${categoryName} yet`}
        />
      </div>
    </div>
  );
}
